import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import EventCalendar from '../../../components/calendar/event-calendar';
import PlayerReservationDetailsModal from '../../modals/PlayerReservationDetailsModal';
import { Player } from '../../../types/players';
import { Reservation } from '../../../types/reservations';
import { Complex, Field, Game } from '../../../types';
import { COMMON_TRANSLATE_KEYS } from '../../../constants/translate-keys';
import GameReservationDetailsModal from '../../modals/GameReservationModal';

type Props = {
  id?: string;
  field: Field;
  games: Game[];
  setGames: (games: Game[]) => void;
  fetchGames: (startDate: Date, endDate: Date) => Promise<void>;
  initialStartDate: Date | null;
  complex: Complex | null;
  newReservation?: Reservation | null;
};

const ArenaCalendar = ({
  id,
  field,
  games,
  setGames,
  fetchGames,
  initialStartDate,
  complex,
  newReservation,
}: Props) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSecondDrawerOpen, setSecondDrawerOpen] = useState(false);

  const [selectedGame, setSelectedGame] =
    useState<Game | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);

  const [startDate, setStartDate] = useState(
    initialStartDate
      ? dayjs(initialStartDate).startOf('week').toDate()
      : dayjs().startOf('week').toDate()
  );
  const [endDate, setEndDate] = useState(
    initialStartDate
      ? dayjs(initialStartDate).endOf('week').toDate()
      : dayjs(startDate).endOf('week').toDate()
  );

  const location = useLocation();

  const { t } = useTranslation();

  useEffect(() => {
    fetchGames(startDate, endDate);
  }, [startDate, endDate, fetchGames, newReservation]);

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => {
    setSecondDrawerOpen(false);
    setIsDrawerOpen(false);
  }

  const openSecondDrawer = () => setSecondDrawerOpen(true);
  const closeSecondDrawer = () => setSecondDrawerOpen(false);

  const handleShowDetails = (game: Game) => {
    setSelectedGame(game);
    openDrawer();
  };

  const handlePlayerClick = (player: Player) => {
    setSelectedPlayer(player);
    openSecondDrawer();
  };

  useEffect(() => {
    if (initialStartDate) {
      setStartDate(dayjs(initialStartDate).startOf('week').toDate());
      setEndDate(dayjs(initialStartDate).endOf('week').toDate());
    }
  }, [initialStartDate]);

  if (!complex || !id) {
    return <h1>{t(COMMON_TRANSLATE_KEYS.UNEXPECTED_ERROR)}</h1>;
  }

  return (
    <>
      <div className="overflow-auto h-full">
        <EventCalendar
          complexName={location.state?.complexName}
          fieldName={location.state?.fieldName}
          games={games}
          complexId={id}
          fieldId={field.id}
          fieldPrice={field.pricePerHour}
          fieldPreferentialPrices={field.preferentialPrices}
          fieldPriceCurrency={field.currencyData}
          onShowDetailsClick={handleShowDetails}
          startDate={startDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          endDate={endDate}
          closingHours={complex.closingHours}
          openingHours={complex.openingHours}
        />
      </div>

      <div>
        <div className="relative z-30">
          <GameReservationDetailsModal
            game={selectedGame}
            closeDrawer={closeDrawer}
            isOpen={isDrawerOpen}
            onPlayerClick={handlePlayerClick}
            fetchReservations={() => fetchGames(startDate, endDate)}
          />
        </div>
      </div>

      {isSecondDrawerOpen && (
        <PlayerReservationDetailsModal
          close={closeSecondDrawer}
          player={selectedPlayer}
          team={selectedGame?.reservation?.team}
        />
      )}
    </>
  );
};

export default ArenaCalendar;
